//TODO: Abstract common part of execution service and websocket base

import { captureException } from '@common/sentry';
import { SentryBreadcrumb, SentryBreadcrumbType } from '@common/sentry/SentryBreadcrumb';
import { ProphecyWSErrorTags, SentryTags } from '@common/sentry/SentryTags';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { Watcher, addWatchableProperty } from '@prophecy/utils/addPrivateProperty';
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { ProphecyError } from '@prophecy/utils/error';
import { getTabId } from '@prophecy/utils/session';
import { addBreadcrumb } from '@sentry/react';
// eslint-disable-next-line no-restricted-syntax
import * as Comlink from 'comlink';
import { WebSocket } from 'partysocket';

import { addCSRFQuery } from '../utils/csrf';
import { getFabricWSURL } from '../utils/getServerUrl';
import { addReplayTokenQuery } from '../utils/replayToken';
import { addShareTokenQuery } from '../utils/shareToken';
import { addTabIdQuery } from '../utils/tabId';
import { ClusterDatabricksOpsResponse } from './apis/types';
import { ExecutionMode } from './execution.types';
import { setSessionFabricMap } from './fabricUtils';
import MockableWebsocket from './websocket';
import { WebSocketWorkerClass } from './WebsocketWorker';

import type { Callback, CallbackParams } from '@LSP/types';

const ComlinkWS = new ComlinkWorker<{ WebSocketWorker: WebSocketWorkerClass }>(
  new URL('../data/WebsocketWorker', import.meta.url)
);

export enum ExecutionServiceNotification {
  open = 'open',
  close = 'close',
  error = 'error',
  connectionError = 'connectionError',
  loadingMessage = 'loadingMessage',
  clusterStatus = 'clusterStatus',
  externalUrl = 'externalUrl',
  sparkMetricsFormat = 'sparkMetricsFormat',
  managementClusterComplete = 'managementClusterComplete',
  managementClusterRestart = 'managementClusterRestart',
  managementClusterStatus = 'managementClusterStatus',
  managementClusterDeleted = 'managementClusterDeleted',
  managementClusterDeletionFailed = 'managementClusterDeletionFailed',
  filesystemListResponse = 'filesystemListResponse',
  filesystemListError = 'filesystemListError',
  databaseListResponse = 'databaseListResponse',
  databaseListError = 'databaseListError',
  tableListResponse = 'tableListResponse',
  functionRefreshResponse = 'functionRefreshResponse',
  tableListError = 'tableListError',
  functionRefreshError = 'functionRefreshError',
  catalogListResponse = 'catalogListResponse',
  catalogListError = 'catalogListError',
  tableSearchResponse = 'searchResponse',
  tableSearchError = 'searchError',
  tableSchemaResponse = 'tableSchemaResponse',
  tableSchemaError = 'tableSchemaError',
  datasetPreviewResponse = 'datasetPreviewResponse',
  workflowExecutionProgress = 'workflowExecutionProgress',
  customExecutionWorkflowComplete = 'customExecutionWorkflowComplete',
  loadDataSampleJobSkipped = 'loadDataSampleJobSkipped',
  workflowExecutionFailed = 'workflowExecutionFailed',
  workflowCancellationComplete = 'workflowCancellationComplete',
  workflowCancellationFailed = 'workflowCancellationFailed',
  jobIdRes = 'jobIdRes',
  interim = 'interim',
  interimComplete = 'interimComplete',
  interimSummary = 'interimSummary',
  paginatedInterim = 'paginatedInterim',
  notifyRWSClosure = 'notifyRWSClosure',

  dataProfilingResponse = 'dataProfilingResponse',
  diffDataSummaryResponse = 'diffDataSummaryResponse',
  diffDataSampleResponse = 'diffDataSampleResponse',
  selectiveRecordsCount = 'selectiveRecordsCount',

  //StepId methods
  executionStepDetails = 'stepDetails',
  executionStepStatusUpdate = 'stepStatus',
  executionStepLog = 'stepLog',

  //Entity runs response
  datasetRunsResponse = 'datasetRunsResponse',
  deleteDatasetRunResponse = 'deleteDatasetRunResponse',
  deletePipelineRunResponse = 'deletePipelineRunResponse',
  pipelineRunsResponse = 'pipelineRunsResponse',
  interims = 'interims',
  historicalRunSnapshotResponse = 'historicalRunSnapshotResponse',
  datasetRunsDetailedResponse = 'datasetRunsDetailedResponse',
  codegenModeOverride = 'codegenModeOverride',
  executionRuntimeOverride = 'executionRuntimeOverride',
  clusterWarnings = 'clusterWarnings',

  //Fabric Configuration
  fabricMisconfigured = 'fabricMisconfigured',

  //Code execution
  executedCode = 'executedCode',
  // secret management
  secretWriteResponse = 'secretWriteResponse',
  secretListResponse = 'secretListResponse',
  secretDeleteResponse = 'secretDeleteResponse',
  secretCrudError = 'secretCrudError',
  hashiCorpHealthResponse = 'hashiCorpHealthResponse',
  // component progress
  gemProgress = 'gemProgress',
  pipelineProgress = 'pipelineProgress',
  submissionProgress = 'submissionProgress',
  submissionComplete = 'submissionComplete',
  compilationStarted = 'compilationStarted',
  compilationCompleted = 'compilationCompleted',

  // workflow upload
  uploadGetSupportedFormatsResponse = 'uploadGetSupportedFormatsResponse',
  uploadFileResponse = 'uploadFileResponse',
  uploadCreationResponse = 'uploadCreationResponse',
  uploadGetSupportedFormatsError = 'uploadGetSupportedFormatsError'
}

export enum ExecutionServiceRequest {
  managementSessionCreation = 'managementSessionCreation',
  managementSessionDeletion = 'managementSessionDeletion',
  filesystemListRequest = 'filesystemListRequest',

  databaseListRequest = 'databaseListRequest',
  functionRefreshRequest = 'functionRefreshRequest',
  tableListRequest = 'tableListRequest',
  catalogListRequest = 'catalogListRequest',
  tableSearchRequest = 'searchRequest',
  tableSchemaRequest = 'tableSchemaRequest',
  datasetPreviewRequest = 'datasetPreviewRequest',
  customExecutionReq = 'customExecutionReq',
  loadMoreInterimsReq = 'loadMoreInterimsReq',
  loadDataSampleReq = 'loadDataSampleReq',
  jobCancelRequest = 'jobCancelRequest',
  preLoadInterims = 'preLoadInterims',

  //Entity page runs
  pipelineRunsRequest = 'pipelineRunsRequest',
  datasetRunsRequest = 'datasetRunsRequest',
  loadHistoricalViewRequest = 'loadHistoricalViewRequest',
  historicalGemProgress = 'historicalGemProgress',
  datasetRunsDetailedRequest = 'datasetRunsDetailedRequest',
  deletePipelineRun = 'deletePipelineRun',
  deleteDatasetRun = 'deleteDatasetRun',

  // secret management
  secretsCrudRequest = 'secretsCrudRequest',

  // send session information
  wsInformation = 'wsInformation',

  // workflow upload
  uploadGetSupportedFormats = 'uploadGetSupportedFormats',
  uploadFileRequest = 'uploadFileRequest',
  uploadCreation = 'uploadCreation'
}

export enum ClusterStatus {
  DETACHED = 'DETACHED',
  ATTACHED = 'ATTACHED',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  ATTACHING = 'ATTACHING'
}

const NOTIFICATION_HISTORY: { [notificationType: string]: boolean } = {
  sparkMetricsFormat: true,
  workflowExecutionProgress: true,
  workflowExecutionComplete: true,
  workflowExecutionFailed: true,
  workflowCancellationComplete: true,
  jobIdRes: true
};

const DEFAULT_LISTENER_CONFIG = {
  latest: true
};

type NotificationList = {
  // TODO: type can be fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [notificationType: string]: Array<any>;
};

type ListenerConfig = {
  latest?: boolean;
};

type NotificationListener = {
  config: ListenerConfig;
  // TODO: type can be fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (data?: any) => void;
};

type ListenersMap = {
  [notificationType: string]: NotificationListener[];
};

type ExecutionServiceOptions = {
  compressionEnabled?: boolean;
  compressionMode?: string;
};

export class ExecutionService {
  private _ws: WebSocket | undefined;

  private listeners: ListenersMap = {};

  // TODO: type can be fixed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private bus: any;

  connected: boolean;

  clusterName: string = '';

  public clusterStatus: ClusterStatus = ClusterStatus.DETACHED;

  private notifications: NotificationList = {};

  private executionUrl: string | undefined;

  private _sessionId: string | undefined;

  private _tabSessionId: string | undefined;

  private _executionMode: ExecutionMode | undefined;

  private _databricksOperationResponse: ClusterDatabricksOpsResponse | undefined;

  private _runtimeOverrides: { [key: string]: string } | undefined;

  private _closeWs: Function | undefined;

  get executionMode(): ExecutionMode | undefined {
    return this._executionMode;
  }

  get runtimeOverrides(): { [key: string]: string } | undefined {
    return this._runtimeOverrides;
  }

  get sessionId(): string | undefined {
    return this._sessionId;
  }

  get databricksOperationResponse(): ClusterDatabricksOpsResponse | undefined {
    return this._databricksOperationResponse;
  }

  get tabSessionId(): string | undefined {
    return this._tabSessionId;
  }

  selectedFabricId: string | undefined;

  watchSelectedFabricId!: Watcher<string | undefined>;

  fabricId: string | undefined;

  compressionEnabled: boolean;

  compressionMode: string | undefined;

  isLongLivedSession!: boolean;

  watchIsLongLivedSession!: Watcher<boolean>;

  isServerless: boolean = false;

  watchIsServerless!: Watcher<boolean>;

  constructor(options?: ExecutionServiceOptions) {
    this.listeners = {};
    this.bus = {};
    this.connected = false;
    this.compressionEnabled = !!options?.compressionEnabled;
    this.compressionMode = options?.compressionMode;

    addWatchableProperty(this, {
      name: 'isLongLivedSession',
      defaultValue: false
    });

    addWatchableProperty(this, {
      name: 'selectedFabricId',
      defaultValue: undefined
    });

    addWatchableProperty(this, {
      name: 'isServerless',
      defaultValue: false
    });
  }

  private setAttachedClusterStatus() {
    this.connected = true;
    this.clusterStatus = ClusterStatus.ATTACHED;
  }

  private async _wsInit() {
    // oxlint-disable-next-line no-async-promise-executor
    return new Promise<void>(async (resolveWsPromise, rejectWsPromise) => {
      if (this._ws) {
        return resolveWsPromise();
      }
      if (!(this.fabricId && this.sessionId && this.executionUrl)) {
        return rejectWsPromise(new ProphecyError(`fabric or session missing`));
      }

      // attach tab id with session id to identify the multiple tabs, replace __ from nanoid to avoid conflict with delimeter
      const wsUrl = getFabricWSURL(this.executionUrl, this.fabricId, this._tabSessionId as string);
      const finalWsUrl = addTabIdQuery(addReplayTokenQuery(addShareTokenQuery(addCSRFQuery(wsUrl))));

      let ws: WebSocket;
      let { _onMessage, _onOpen, _onClose, _onError } = this;
      const onWsOpen = (...args: []) => {
        _onOpen.apply(this, args);
        resolveWsPromise();
      };

      let onWsError = (...args: [Event]) => {
        _onError.apply(this, args);
        rejectWsPromise(...args);
      };

      if (this.compressionEnabled) {
        ws = await new ComlinkWS.WebSocketWorker(
          finalWsUrl,
          undefined,
          Comlink.proxy(onWsOpen),
          undefined,
          this.compressionMode
        );

        _onMessage = Comlink.proxy(_onMessage);
        _onClose = Comlink.proxy(_onClose);
        onWsError = Comlink.proxy(onWsError);
      } else {
        // we handle open event for compression enabled case in constructor for websocket worker
        // for non compression case we handle it normally
        ws = new MockableWebsocket(finalWsUrl, undefined);
        ws.addEventListener('open', onWsOpen);
      }

      ws.addEventListener('message', _onMessage);
      ws.addEventListener('close', _onClose);
      ws.addEventListener('error', onWsError);

      this._ws = ws;

      // method to close websocket
      this._closeWs = function () {
        ws.removeEventListener('message', _onMessage);
        ws.removeEventListener('open', onWsOpen);
        ws.removeEventListener('close', _onClose);
        ws.removeEventListener('error', onWsError);
        ws.close();
        this._ws = undefined;
        this._closeWs = undefined;
      };

      // reset executionMode before connecting next cluster instead of on detach or destroy
      //  as we are showing interims even after detach or destroy
      this._executionMode = undefined;
      this._runtimeOverrides = undefined;
      this.listen(
        ExecutionServiceNotification.codegenModeOverride,
        ({ params }: { params?: { mode: string | undefined } }) => {
          this._executionMode = params?.mode as ExecutionMode | undefined;
        }
      );
      this.listen(
        ExecutionServiceNotification.executionRuntimeOverride,
        ({ params }: { params?: { [key: string]: string } }) => {
          this._runtimeOverrides = params;
        }
      );
    });
  }

  private _onOpen = () => {
    this.connected = true;
    this.clusterStatus = ClusterStatus.ATTACHING;

    this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    this.listeners[ExecutionServiceNotification.open]?.forEach((l) => l.callback());
  };

  private _onClose = () => {
    this.connected = false;
    if (this.clusterStatus !== ClusterStatus.CONNECTION_ERROR) {
      this.clusterStatus = ClusterStatus.DETACHED;
    }
    this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    this.listeners[ExecutionServiceNotification.close]?.forEach((l) => l.callback());

    this._closeWs?.();
  };

  private _onError = (exception: Event) => {
    captureException({
      exception,
      errorTags: { [SentryTags.ProphecyErrorWSType]: ProphecyWSErrorTags.ExecutionDisconnect }
    });
    this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    console.error(exception);
    this.listeners[ExecutionServiceNotification.connectionError]?.forEach((l) => l.callback(exception));
  };

  private _onMessage = (event: MessageEvent) => {
    let messageData = event.data;

    if (this.compressionEnabled) {
      messageData = new TextDecoder().decode(messageData);
    }

    const data = JSON.parse(messageData);

    const { msg: params, msgType: notificationType } = data;
    addBreadcrumb({
      type: SentryBreadcrumbType.wss,
      category: SentryBreadcrumb.ExecutionNotification,
      message: JSON.stringify({ method: notificationType }),
      level: 'info'
    });
    let updatedClusterStatus;
    if (
      notificationType === ExecutionServiceNotification.managementClusterDeleted &&
      this.clusterStatus !== ClusterStatus.CONNECTION_ERROR
    ) {
      updatedClusterStatus = ClusterStatus.DETACHED;
    } else if (notificationType === ExecutionServiceNotification.managementClusterRestart) {
      updatedClusterStatus = ClusterStatus.ATTACHING;
    } else if (notificationType === ExecutionServiceNotification.managementClusterComplete) {
      updatedClusterStatus = ClusterStatus.ATTACHED;
    }

    if (updatedClusterStatus) {
      this.clusterStatus = updatedClusterStatus;
      this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    }

    // maintain notification history
    if (NOTIFICATION_HISTORY[notificationType]) {
      // initialize history if not present already
      const notifications = this.notifications[notificationType] || [];

      this.notifications[notificationType] = [...notifications, params];
    }

    // handle notification
    const listeners = this.listeners[notificationType];
    if (listeners) {
      listeners.forEach(({ config, callback }) => {
        let data = params;
        if (config.latest === false) {
          // if its on history, provide the list from there or just send the latest data
          data = this.notifications[notificationType] || [params];
        }

        callback({ method: notificationType, params: data });
      });
    }
  };

  private _clearHistory = (notificationType: string) => {
    const history = this.notifications[notificationType];

    // if there is no history for a notification type don't do anything
    if (!history?.length) return;

    // reset history
    this.notifications[notificationType] = [];

    const listeners = this.listeners[notificationType];

    // notify listeners about flush
    listeners.forEach(({ config, callback }) => {
      const data = config.latest ? undefined : [];
      callback({ method: notificationType, params: data });
    });
  };

  retry() {
    this._wsInit();
  }

  setClusterStatus(status: ClusterStatus) {
    this.clusterStatus = status;
    this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
  }

  setSelectedFabric(fabricId: string | undefined) {
    this.selectedFabricId = fabricId;
  }

  async connect({
    executionUrl,
    fabricId,
    sessionId,
    name,
    isLongLivedSession,
    storeInLocalStorage,
    tabSpecificSessionId,
    projectId,
    databricksOperationResponse,
    isServerless = false
  }: {
    executionUrl: string;
    fabricId: string;
    sessionId: string;
    name: string;
    projectId?: string;
    isLongLivedSession: boolean;
    storeInLocalStorage: boolean;
    tabSpecificSessionId: boolean;
    databricksOperationResponse?: ClusterDatabricksOpsResponse;
    isServerless: boolean;
  }) {
    if (this.fabricId && this.sessionId && (this.sessionId !== sessionId || this.fabricId !== fabricId)) {
      this.detach();
    }
    this.clusterName = name;

    //For metadata page show the connected session label in case of autoConnect
    this.isLongLivedSession = isLongLivedSession;
    this.isServerless = isServerless;
    this.executionUrl = executionUrl;
    this.fabricId = fabricId;
    this._sessionId = sessionId;
    this._databricksOperationResponse = databricksOperationResponse;
    this._tabSessionId = tabSpecificSessionId ? `${this.sessionId}__${getTabId().replaceAll('__', '_')}` : sessionId;
    await this._wsInit();

    if (storeInLocalStorage) {
      // store the last connected session id and fabricId in local storage
      setSessionFabricMap(sessionId, fabricId, projectId, isServerless);
    }
  }

  destroy() {
    /**
     * for destroy, don't close the websocket.
     * BE will close it from their side and
     * plus FE also listens for `managementClusterDeleted` notification which will come after executionService.destroy is called
     * */
    this.connected = false;
    this.clusterStatus = ClusterStatus.DETACHED;
    this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    this.listeners[ExecutionServiceNotification.close]?.forEach((l) => l.callback());

    this.notifications = {};
    this._sessionId = undefined;
    this._executionMode = undefined;
    this.isLongLivedSession = false;
    this.isServerless = false;
    if (this._ws) {
      this._ws = undefined;
    }
  }

  detach() {
    this._onClose();
    this.notifications = {};
    this._sessionId = undefined;
    this._tabSessionId = undefined;
    this._executionMode = undefined;
    this.isLongLivedSession = false;
    this.isServerless = false;
  }

  send({ method: notificationType, params = {} }: CallbackParams<ExecutionServiceRequest>) {
    // if any send is called before the ws is initialized, it may be be coming due to previous broken connection, that can be ignored.
    // also after new connection is established, the state is reset, so we don't need to queue this messages.
    if (!this.connected) return;

    if (notificationType === ExecutionServiceRequest.managementSessionCreation) {
      this.clusterStatus = ClusterStatus.ATTACHING;
      this.listeners[ExecutionServiceNotification.clusterStatus]?.forEach((l) => l.callback(this.clusterStatus));
    }
    if (this._ws) {
      let messageData: string | Uint8Array<ArrayBuffer> = JSON.stringify({
        msgType: notificationType,
        msg: params
      });
      addBreadcrumb({
        type: SentryBreadcrumbType.wss,
        category: SentryBreadcrumb.ExecutionRequest,
        message: JSON.stringify({ method: notificationType }),
        level: 'info'
      });
      if (this.compressionEnabled) {
        messageData = new TextEncoder().encode(messageData);
        messageData = Comlink.transfer(messageData, [messageData.buffer]);
      }

      this._ws.send(messageData);
    } else {
      /**
       * In execution service the send method can called after some async routine, like
       * - Play button is enabled as connection is there, user clicked, while edweb is generating the payload, connection is lost
       * - User loads and we connect to execution service, there may be exisiting job running fow which we show dialog,
       * while user is interacting with dialog, connection is lost
       *
       * So in this cases we don't want to throwup error, instead we should inform user that connection is lost
       * But to avoid case where user is trying to send data on unconnected websocket in normal case, we throw error in dev mode
       */
      if (!isNodeEvnProduction()) {
        throw new ProphecyError('Websocket is not connected');
      } else {
        toast.error({ content: 'Execution service got disconnected, please try again' });
      }
    }
  }

  listen(
    notificationType: ExecutionServiceNotification,
    callback: Callback<ExecutionServiceNotification>,
    config?: ListenerConfig
  ) {
    config = { ...DEFAULT_LISTENER_CONFIG, ...config };
    const listner = {
      callback,
      config
    };
    if (this.listeners[notificationType]) {
      this.listeners[notificationType].push(listner);
    } else {
      this.listeners[notificationType] = [listner];
    }

    // if there is historical data for notificationType type send that while connecting
    const history = this.notifications[notificationType];
    if (history?.length) {
      // if consumer is asking for complete history send all data, or else just send the latest data
      const data = config.latest ? history[history.length - 1] : history;

      callback({ method: notificationType, params: data });
    }

    return () => {
      this.listeners[notificationType] = this.listeners[notificationType].filter(
        (listener) => listener.callback !== callback
      );
    };
  }

  flush(...notificationTypes: ExecutionServiceNotification[]) {
    if (notificationTypes.length > 0) {
      notificationTypes.forEach((notificationType) => {
        this._clearHistory(notificationType);
      });
    } else {
      Object.keys(this.notifications).forEach(this._clearHistory);
    }
  }
}
